import {
  Box,
  Text,
  Badge,
  SimpleGrid,
  HStack,
  VStack,
  Divider,
  Button,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { Package } from 'lucide-react';
import type { ReactNode } from 'react';

export type ProdutoAtributo = {
  id?: string | number;
  atributo_externo_id?: string | number;
  name?: string;
  nome?: string;
  value_name?: string;
  valor_nome?: string;
  value?: string;
};

export type ProdutoDetalhado = {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  categoria: string;
  preco: string | number;
  promo?: string | number;
  estoque?: number | string;
  estoque_atual?: number | string;
  estoque_disponivel?: number | string;
  status: boolean;
  vendas: number;
  canais: string[];
  codigo_barras?: string;
  descricao?: string;
  peso?: number | string;
  altura?: number | string;
  largura?: number | string;
  profundidade?: number | string;
  atributos_ml?: ProdutoAtributo[];
};

type ProdutoFormProps = {
  produto?: ProdutoDetalhado | null;
  onSincronizar?: () => void;
  onClose?: () => void;
  onAtualizarStatus?: (id: number, ativo: boolean) => void | Promise<void>;
};

type InfoProps = { label: string; value?: ReactNode };
type CardProps = {
  title: string;
  value: ReactNode;
  extra?: ReactNode;
  highlight?: boolean;
};
type DimensaoCardProps = { label: string; value: ReactNode };

const formatarvalor = (value?: string | number | null) => {
  if (!value) return 'R$ 0,00';
  const number = Number(value.toString().replace('R$', '').replace(',', '.'));
  return `R$ ${number.toFixed(2).replace('.', ',')}`;
};

const ProdutoForm = ({
  produto,
  onSincronizar,
  onClose,
  onAtualizarStatus,
}: ProdutoFormProps) => {
  const corTexto = useColorModeValue('gray.800', 'white');
  const corTextoSecundario = useColorModeValue('gray.600', 'gray.300');
  const corTextoSuave = useColorModeValue('gray.500', 'gray.400');
  const fundoCard = useColorModeValue('white', 'gray.700');
  const corBorda = useColorModeValue('gray.200', 'gray.600');
  const fundoCanal = useColorModeValue('green.50', 'green.900');

  const Info = ({ label, value }: InfoProps) => (
    <Box>
      <Text fontSize="xs" color={corTextoSuave}>
        {label}
      </Text>
      <Text fontWeight="500" color={corTexto}>
        {value || '-'}
      </Text>
    </Box>
  );

  const Card = ({ title, value, extra, highlight }: CardProps) => (
    <Box
      p={4}
      borderWidth="1px"
      borderColor={corBorda}
      borderRadius="lg"
      bg={fundoCard}
    >
      <Text fontSize="xs" color={corTextoSuave}>
        {title}
      </Text>
      <Box fontWeight="bold" color={highlight ? 'green.400' : corTexto}>
        {value}
      </Box>
      {extra && <Box mt={1}>{extra}</Box>}
    </Box>
  );

  const DimensaoCard = ({ label, value }: DimensaoCardProps) => (
    <Box
      textAlign="center"
      p={3}
      borderRadius="xl"
      borderWidth={1}
      borderColor={corBorda}
      bg={fundoCard}
    >
      <Text fontSize="xs" color={corTextoSecundario}>
        {label}
      </Text>
      <Text fontWeight="bold" color={corTexto}>
        {value}
      </Text>
    </Box>
  );

  if (!produto) return null;

  const atributos = Array.isArray(produto.atributos_ml)
    ? produto.atributos_ml
    : [];

  const atributosPreenchidos = atributos.filter(
    attr => attr.value_name || attr.valor_nome || attr.value,
  ).length;

  const atributosPendentes = Math.max(
    atributos.length - atributosPreenchidos,
    0,
  );

  const estoqueTotal = Number(produto.estoque_atual ?? produto.estoque ?? 0);

  const estoqueDisponivel = Number(
    produto.estoque_disponivel ?? produto.estoque_atual ?? produto.estoque ?? 0,
  );

  return (
    <Box color={corTexto}>
      <HStack justify="space-between" mb={2}>
        <HStack>
          <Box p={3} borderRadius="lg" display="flex" alignItems="center">
            <Icon as={Package} boxSize={6} color="blue.400" />
          </Box>
          <Box>
            <Text fontWeight="bold" fontSize="lg" color={corTexto}>
              {produto.nome}
            </Text>
            <Text fontSize="sm" color={corTextoSuave}>
              SKU: {produto.sku}
            </Text>
          </Box>
        </HStack>
      </HStack>

      <HStack spacing={3} mb={4}>
        <Text fontSize="sm" fontWeight="500" color={corTexto}>
          Status:
        </Text>
        <Badge
          colorScheme={produto.status ? 'green' : 'red'}
          borderRadius="full"
        >
          {produto.status ? 'Ativo' : 'Inativo'}
        </Badge>
        <Button
          size="sm"
          colorScheme={produto.status ? 'red' : 'green'}
          onClick={() => onAtualizarStatus?.(produto.id, !produto.status)}
        >
          {produto.status ? 'Desativar' : 'Ativar'}
        </Button>
      </HStack>

      <Box
        p={4}
        borderRadius="lg"
        mb={4}
        borderWidth="1px"
        borderColor={corBorda}
        bg={fundoCard}
      >
        <SimpleGrid columns={2} spacing={4}>
          <Info label="Código Interno" value={produto.id} />
          <Info label="Código de Barras" value={produto.codigo_barras} />
          <Info label="Categoria" value={produto.categoria} />
          <Info label="Marca" value={produto.marca} />
        </SimpleGrid>
      </Box>

      <Box mb={4}>
        <Text fontWeight="bold" color={corTexto}>
          Descrição
        </Text>
        <Text fontSize="sm" color={corTextoSecundario}>
          {produto.descricao || 'Produto sem descrição cadastrada.'}
        </Text>
      </Box>

      <SimpleGrid columns={3} spacing={4} mb={4}>
        <Card
          title="Preço"
          value={
            <Text color={corTextoSecundario}>
              {formatarvalor(produto.preco)}
            </Text>
          }
          extra={
            produto.promo ? (
              <Text color="green.400" fontSize="xs" fontWeight="medium">
                Promo: {formatarvalor(produto.promo)}
              </Text>
            ) : null
          }
        />
        <Card
          title="Estoque Total"
          value={
            <Text color={estoqueTotal > 0 ? 'green.400' : 'red.400'}>
              {`${estoqueTotal} un`}
            </Text>
          }
        />
        <Card
          title="Disponível"
          value={
            <Text color={corTextoSecundario}>{`${estoqueDisponivel} un`}</Text>
          }
          highlight
        />
      </SimpleGrid>

      <Box mb={4}>
        <Text fontWeight="bold" mb={2} color={corTexto}>
          Dimensões e Peso
        </Text>
        <SimpleGrid columns={4} spacing={4}>
          <DimensaoCard
            label="Peso"
            value={produto.peso ? `${produto.peso}kg` : '-'}
          />
          <DimensaoCard
            label="Altura"
            value={produto.altura ? `${produto.altura}cm` : '-'}
          />
          <DimensaoCard
            label="Largura"
            value={produto.largura ? `${produto.largura}cm` : '-'}
          />
          <DimensaoCard
            label="Profundidade"
            value={produto.profundidade ? `${produto.profundidade}cm` : '-'}
          />
        </SimpleGrid>
      </Box>

      <Box mb={4}>
        <Text fontWeight="bold" mb={2} color={corTexto}>
          Atributos
        </Text>

        <SimpleGrid columns={2} spacing={4} mb={3}>
          <Card
            title="Preenchidos"
            value={`${atributosPreenchidos} atributos`}
          />
          <Card title="Pendentes" value={`${atributosPendentes} atributos`} />
        </SimpleGrid>

        <VStack spacing={2} align="stretch">
          {atributos.length > 0 ? (
            atributos.slice(0, 5).map(attr => (
              <HStack
                key={attr.id || attr.atributo_externo_id}
                justify="space-between"
                borderWidth="1px"
                borderColor={corBorda}
                borderRadius="md"
                p={3}
                bg={fundoCard}
              >
                <Text fontSize="sm" color={corTextoSuave}>
                  {attr.name ||
                    attr.nome ||
                    attr.id ||
                    attr.atributo_externo_id}
                </Text>

                <Text fontSize="sm" fontWeight="600" color={corTexto}>
                  {attr.value_name || attr.valor_nome || attr.value || '-'}
                </Text>
              </HStack>
            ))
          ) : (
            <Text fontSize="sm" color={corTextoSuave}>
              Nenhum atributo preenchido.
            </Text>
          )}
        </VStack>
      </Box>

      <Box mb={4}>
        <Text fontWeight="bold" mb={2} color={corTexto}>
          Canais de Venda
        </Text>
        <VStack spacing={3}>
          {produto.canais?.length > 0 ? (
            produto.canais.map((c: string) => (
              <HStack
                key={c}
                justify="space-between"
                w="100%"
                p={3}
                bg={fundoCanal}
                border="1px solid"
                borderColor={corBorda}
                borderRadius="lg"
              >
                <Text fontSize="sm" color={corTexto}>
                  ✔ {c}
                </Text>
                <Badge colorScheme="green">Publicado</Badge>
              </HStack>
            ))
          ) : (
            <Text fontSize="sm" color="red.400">
              Produto não anunciado
            </Text>
          )}
        </VStack>
      </Box>

      <Divider my={4} borderColor={corBorda} />

      <HStack justify="space-between">
        <Box>
          <Text fontSize="sm" color={corTexto}>
            Total de vendas: {produto.vendas}
          </Text>
          <Text fontSize="xs" color={corTextoSuave}>
            Sincronizado: 25/03/2026
          </Text>
        </Box>
        <HStack>
          {onClose && (
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
          )}
          <Button colorScheme="blue" onClick={onSincronizar}>
            Sincronizar
          </Button>
        </HStack>
      </HStack>
    </Box>
  );
};

export default ProdutoForm;
